import React, { FC, useState, useEffect, Fragment } from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { deleteInvoiceNoteTemplate } from 'apollo/templates/invoices';
import { DocumentNode, NetworkStatus, useLazyQuery, useMutation, ApolloCache, Reference } from '@apollo/client';
import InfiniteScroll from 'react-infinite-scroll-component';
import Loader from 'components/shared/loader/loader.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import DebitCreditNoteItem from '../debit-credit-note-item/debit-credit-note-item.component';
import ConfirmationDialog from 'components/orders/shared/confirmation-dialog.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { InvoiceNoteFilter, InvoiceNote } from 'models/invoice';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';

type DebitCreditNotesListProps = {
    invoiceNoteFilter: InvoiceNoteFilter;
    queryTemplate: DocumentNode;
    queryName: string;
    handleNoteClick: (debitCreditNote: InvoiceNote) => void;
    updatedNotes: InvoiceNote;
};

const DebitCreditNotesList: FC<DebitCreditNotesListProps> = ({
    invoiceNoteFilter,
    queryTemplate,
    queryName,
    handleNoteClick,
    updatedNotes,
}: DebitCreditNotesListProps) => {
    const { t: translate } = useTranslation(['common', 'orders', 'invoices']);
    const [lastOffset, setLastOffset] = useState(0);
    const [invoiceNotes, setInvoiceNotes] = useState<InvoiceNote[]>([]);
    const [invoiceNoteId, setInvoiceNoteId] = useState('');
    const [open, setOpen] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const getFormattedFilters = (invoiceNoteFilter: InvoiceNoteFilter) => {
        const filters = { ...invoiceNoteFilter };
        delete filters.clinic;
        return filters;
    };

    const [getInvoiceNotes, { loading, fetchMore, networkStatus }] = useLazyQuery(queryTemplate, {
        notifyOnNetworkStatusChange: true,
        onCompleted: (data) => {
            const invoiceNotes: InvoiceNote[] = data[queryName] || [];
            setInvoiceNotes(invoiceNotes);
            setLastOffset(invoiceNotes?.length);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        getInvoiceNotes({
            variables: {
                filters: getFormattedFilters(invoiceNoteFilter),
                limit: 20,
                offset: 0,
            },
        });
    }, [invoiceNoteFilter]);

    useEffect(() => {
        setInvoiceNotes([updatedNotes, ...invoiceNotes]);
    }, [updatedNotes]);

    const [deleteInvoiceNote, { loading: isInvoiceNoteDeletionInProgress, error: invoiceNoteDeletionErrors }] =
        useMutation(deleteInvoiceNoteTemplate, {
            onCompleted: () => {
                const notes = JSON.parse(JSON.stringify(invoiceNotes));
                const index = notes.findIndex((note: InvoiceNote) => note._id === invoiceNoteId);
                if (index !== -1) {
                    notes.splice(index, 1);
                    setInvoiceNotes(notes);
                }
            },
            update(cache: ApolloCache<InvoiceNote[]>) {
                cache.modify({
                    fields: {
                        invoiceNotes(existingInvoiceNoteRefs: Reference | (Reference | undefined)[], { readField }) {
                            if (existingInvoiceNoteRefs instanceof Array) {
                                return [...existingInvoiceNoteRefs].filter((invoiceNoteRef: Reference | undefined) => {
                                    return invoiceNoteId !== readField('_id', invoiceNoteRef);
                                });
                            }
                            return [];
                        },
                    },
                });
            },
            onError(error) {
                showBoundary(
                    error && (error?.graphQLErrors[0]?.extensions?.exception as any)?.type !== 'BAD_REQUEST'
                        ? error
                        : undefined,
                );
            },
        });

    const handleFetchMore = () => {
        if (fetchMore) {
            fetchMore({
                variables: {
                    offset: lastOffset,
                },
            });
        } else {
            getInvoiceNotes({
                variables: {
                    filters: getFormattedFilters(invoiceNoteFilter),
                    limit: 20,
                    offset: lastOffset,
                },
            });
        }
    };

    const handleDeleteNote = async (invoiceNoteId: string) => {
        setInvoiceNoteId(invoiceNoteId);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleOk = async () => {
        await deleteInvoiceNote({
            variables: { invoiceNoteId: invoiceNoteId },
        });
        setOpen(false);
    };

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(handleFetchMore, invoiceNotes?.length || 0);

    if ((loading && networkStatus !== NetworkStatus.fetchMore && isInvoiceNoteDeletionInProgress) || !invoiceNotes) {
        return <Loader />;
    }
    return (
        <Fragment>
            <TableContainer component={Paper}>
                <InfiniteScroll
                    dataLength={invoiceNotes ? invoiceNotes.length : 0}
                    next={handleFetchMore}
                    throttleTimeInMs={75}
                    hasMore={true}
                    loader={undefined}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell>{translate('invoices:INVOICE')}</TableCell>
                                <TableCell>{translate('common:CLINIC')}</TableCell>
                                <TableCell>{translate('common:DESCRIPTION')}</TableCell>
                                <TableCell>{translate('orders:QUANTITY')}</TableCell>
                                <TableCell>{translate('orders:UNIT_PRICE')}</TableCell>
                                <TableCell>{translate('orders:TOTAL_EXCL_VAT')}</TableCell>
                                <TableCell>{translate('orders:VAT_PERCENT')}</TableCell>
                                <TableCell>{translate('orders:TOTAL_INCL_VAT')}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoiceNotes.map((invoiceNote: InvoiceNote, index: number) => (
                                <DebitCreditNoteItem
                                    key={index}
                                    debitCreditNote={invoiceNote}
                                    handleNoteClick={handleNoteClick}
                                    handleDeleteNote={handleDeleteNote}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </InfiniteScroll>
            </TableContainer>
            {invoiceNoteDeletionErrors &&
            (invoiceNoteDeletionErrors?.graphQLErrors[0]?.extensions?.exception as any)?.type === 'BAD_REQUEST' ? (
                <AlertSnackbar type="error" message={invoiceNoteDeletionErrors?.message} />
            ) : undefined}
            <ConfirmationDialog
                open={open}
                handleClose={handleClose}
                handleOk={handleOk}
                title={translate('invoices:DELETE_NOTE')}
                confirm={translate('invoices:CONFIRM_DELETE_NOTE')}
                process={translate('common:DELETE')}
            />
        </Fragment>
    );
};

export default DebitCreditNotesList;
